import { Thead, useColorModeValue } from '@chakra-ui/react';
import type { TableHeadProps, PositionProps } from '@chakra-ui/react';
import throttle from 'lodash/throttle';
import React from 'react';

interface Props extends TableHeadProps {
  top?: number;
  children?: React.ReactNode;
  stickySurface?: boolean;
}

const TheadSticky = ({ top, children, stickySurface, sx, ...restProps }: Props) => {
  const ref = React.useRef<HTMLTableSectionElement>(null);
  const [ isSticky, setIsSticky ] = React.useState(false);
  const bgColor = useColorModeValue('white', 'black');
  const borderColor = useColorModeValue('gray.200', '#222222');
  const stickySurfaceShadow = `0 0 0 8px ${ bgColor }`;
  let boxShadow = 'none';

  if (isSticky) {
    boxShadow = stickySurface ? `${ stickySurfaceShadow }, var(--chakra-shadows-md)` : 'md';
  }

  const handleScroll = React.useCallback(() => {
    if (Number(ref.current?.getBoundingClientRect().y) <= (top || 0)) {
      setIsSticky(true);
    } else {
      setIsSticky(false);
    }
  }, [ top ]);

  React.useEffect(() => {
    const throttledHandleScroll = throttle(handleScroll, 300);

    window.addEventListener('scroll', throttledHandleScroll);

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [ handleScroll ]);

  const props = {
    ...restProps,
    position: 'sticky' as PositionProps['position'],
    top: top ? `${ top }px` : 0,
    backgroundColor: bgColor,
    boxShadow,
    zIndex: '1',
    sx: stickySurface ? {
      '& tr': {
        backgroundColor: bgColor,
      },
      '& th': {
        backgroundColor: bgColor,
        backgroundClip: 'padding-box',
        boxShadow: `inset 0 -1px 0 ${ borderColor }`,
      },
      ...sx,
    } : sx,
  };

  return (
    <Thead { ...props } ref={ ref }>
      { children }
    </Thead>
  );
};

export default TheadSticky;
